import { Dayjs } from 'dayjs';
import {
  DataModelBuilder, SerializationContext, SerializedData,
} from '../../dto/data-model.js';
import { BusinessModuleRef } from '../business-module-ref.js';
import { BusinessLevelMarketingSubscription } from './business-level-marketing-subscription.js';
import { MapUtils } from '../../../utility/map-utils.js';
import { cloneData, cloneList, isSameData, isSameList } from '../../dto/data-model-utils.js';
import { BusinessLevelMarketingSubscriptionType } from './business-level-marketing-subscription-type.js';
import { TimeZone } from '../../../time/timezone.js';
import { fromEnumAsString } from '../../../serialization/enum-utils.js';
import { JSONUtils } from '../../../utility/json-utils.js';

export class MailchimpSubscription extends BusinessLevelMarketingSubscription {
  static readonly TYPE_ID: string = 'ab05df3997494fe5bc5ea3a7ab67af9d';

  private constructor(
    public readonly id: string,
    public readonly emailAddress: string,
    public readonly uniqueEmailId: string,
    public readonly contactId: string,
    public readonly webId: number,
    public readonly listId: string,
    public readonly tags: string[],
    public readonly businessRef: BusinessModuleRef,
    public readonly consentDate: Dayjs,
    public readonly subscriptionDate: Dayjs,
  ) {
    super();
  }

  get $typeId(): string {
    return MailchimpSubscription.TYPE_ID;
  }

  get type(): BusinessLevelMarketingSubscriptionType {
    return BusinessLevelMarketingSubscriptionType.Mailchimp;
  }

  static create(args: {
    id: string,
    emailAddress: string,
    uniqueEmailId: string,
    contactId: string,
    webId: number,
    listId: string,
    tags: string[],
    businessRef: BusinessModuleRef,
    consentDate: Dayjs,
    subscriptionDate: Dayjs,
  }): MailchimpSubscription {
    return new MailchimpSubscription(
        args.id,
        args.emailAddress,
        args.uniqueEmailId,
        args.contactId,
        args.webId,
        args.listId,
        args.tags,
        args.businessRef,
        args.consentDate,
        args.subscriptionDate,
    );
  }

  static fromMap(context: SerializationContext, map: SerializedData): MailchimpSubscription {
    return MailchimpSubscription.create({
      id: MapUtils.getString(map, 'id'),
      emailAddress: MapUtils.getString(map, 'emailAddress'),
      uniqueEmailId: MapUtils.getString(map, 'uniqueEmailId'),
      contactId: MapUtils.getString(map, 'contactId'),
      webId: MapUtils.getNumber(map, 'webId'),
      listId: MapUtils.getString(map, 'listId'),
      tags: MapUtils.getList<string>(map, 'tags', (value) => JSONUtils.decodeString(value)),
      businessRef: BusinessModuleRef.mustDecode(MapUtils.getString(map, 'businessRef')),
      consentDate: context.decodeDate(MapUtils.getNumber(map, 'consentDate')),
      subscriptionDate: context.decodeDate(MapUtils.getNumber(map, 'subscriptionDate')),
    });
  }

  toMap(context: SerializationContext): SerializedData {
    return {
      type: fromEnumAsString(this.type),
      id: this.id,
      emailAddress: this.emailAddress,
      uniqueEmailId: this.uniqueEmailId,
      contactId: this.contactId,
      webId: this.webId,
      listId: this.listId,
      tags: this.tags,
      businessRef: this.businessRef.encode(),
      consentDate: context.encodeDate(this.consentDate),
      subscriptionDate: context.encodeDate(this.subscriptionDate),
    };
  }

  clone(): this {
    return MailchimpSubscription.create({
      id: this.id,
      emailAddress: this.emailAddress,
      uniqueEmailId: this.uniqueEmailId,
      contactId: this.contactId,
      webId: this.webId,
      listId: this.listId,
      tags: cloneList(this.tags),
      businessRef: cloneData(this.businessRef),
      consentDate: TimeZone.cloneDate(this.consentDate),
      subscriptionDate: TimeZone.cloneDate(this.subscriptionDate),
    }) as this;
  }

  isSame(other: this): boolean {
    if(other === this) {
      return true;
    }

    return other.id === this.id &&
      other.emailAddress === this.emailAddress &&
      other.uniqueEmailId === this.uniqueEmailId &&
      other.contactId === this.contactId &&
      other.webId === this.webId &&
      other.listId === this.listId &&
      isSameList(other.tags, this.tags) &&
      isSameData(other.businessRef, this.businessRef) &&
      other.consentDate === this.consentDate &&
      other.subscriptionDate === this.subscriptionDate;
  }
}

export class MailchimpSubscriptionBuilder extends DataModelBuilder<MailchimpSubscription> {
  build(context: SerializationContext, map: SerializedData): MailchimpSubscription {
    return MailchimpSubscription.fromMap(context, map);
  }
}
